import {
  DetailedHTMLProps,
  FocusEventHandler,
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import Skeleton from "react-loading-skeleton";
import {
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../utils/constants";
import { Icon } from "../icons/icon";
import { ErrorText } from "../typography/errorText";

export type TextInputProps = {
  type: string;
  name: string;
  value?: string;
  placeholder: string;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onKeyDown?: (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  hasIcon?: boolean;
  iconSvg?: string;
  iconClassName?: string;
  errors?: string;
  rows?: number;
  inputClass?: string;
  className?: string;
  containerClassName?: string;
  customIcon?: ReactNode;
  customIconPosition?: "left" | "right";
  hasTitle?: boolean;
  title?: string;
  autoComplete?: string;
  isDropdown?: boolean;
  loading?: boolean;
} & DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

export const TextInput = ({
  type = "text",
  name,
  value,
  placeholder = "Placeholder",
  onChange,
  onBlur,
  hasIcon = true,
  iconSvg,
  iconClassName,
  errors,
  rows = 3,
  inputClass = "",
  className = "",
  containerClassName = "",
  customIcon = undefined,
  customIconPosition = "left",
  hasTitle = false,
  title = "",
  autoComplete = "off",
  isDropdown = false,
  loading = false,
  ...inputProps
}: TextInputProps) => {
  const [viewPassword, setViewPassword] = useState(false);

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const getIcon = () => {
    if (type === "password") {
      return "lock";
    } else {
      return iconSvg ?? "sms";
    }
  };

  const icon = getIcon();

  const inputClassName = `
  bg-transparent w-full
  outline-none focus:outline-none
  placeholder:text-pry-dark-green-300/50
  autofill:focus:bg-transparent autofill:bg-transparent
  ${inputClass}
  `;

  useEffect(() => {
    const textarea = textAreaRef.current;
    if (textarea) {
      // Reset the height to auto to shrink if content is deleted
      textarea.style.height = "auto";
      // Set the height to match scrollHeight
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [value]);

  return loading ? (
    <TextInputLoader hasTitle={hasTitle} className={containerClassName} />
  ) : (
    <div className={containerClassName}>
      {hasTitle && (
        <p className={`text-sm text-pry-dark-green-300 font-medium mb-1`}>
          {title}
        </p>
      )}

      <div
        className={`w-full
          px-[17px] py-[14px]
          flex flew-row items-center gap-4
          text-sm font-medium
          bg-light-green-bg 
          border rounded-lg border-transparent
          focus-within:border-pry-green-200
          ${errors && "focus-within:!border-red-200 !bg-light-red-bg"}
          transition-all
          ${className}
          `}
      >
        {hasIcon && !customIcon ? (
          <Icon svg={icon} width={20} height={20} className="w-[24px]" />
        ) : undefined}

        {customIcon && customIconPosition === "left" ? customIcon : undefined}

        {type !== "textarea" ? (
          <input
            type={viewPassword ? "text" : type}
            name={name}
            value={value ?? ""}
            placeholder={placeholder}
            onChange={onChange}
            onBlur={onBlur}
            className={inputClassName}
            autoComplete={autoComplete}
            {...inputProps}
          />
        ) : (
          <textarea
            name={name}
            value={value}
            placeholder={placeholder}
            onChange={onChange}
            onBlur={onBlur}
            rows={rows}
            className={`${inputClassName} resize-none`}
            ref={textAreaRef}
          />
        )}

        {type === "password" && (
          <Icon
            svg={viewPassword ? "hide" : "view"}
            width={20}
            height={20}
            className={`
            w-[24px] 
            cursor-pointer
            transition-all
            duration-500
            ${viewPassword && "rotate-[180deg]"}
            `}
            onClick={() => {
              setViewPassword(!viewPassword);
            }}
          />
        )}

        {customIcon && customIconPosition === "right" ? customIcon : undefined}

        {isDropdown ? (
          <button
            type="button"
            onClick={() => {
              const input = document.querySelector<HTMLInputElement>(
                `input[name="${name}"]`,
              );
              input?.click();
              input?.focus();
            }}
          >
            <Icon svg="caret-down" width={20} height={20} className="" />
          </button>
        ) : undefined}
      </div>

      {errors && <ErrorText text={errors} />}
    </div>
  );
};

const TextInputLoader = ({
  hasTitle,
  className,
}: {
  hasTitle: boolean;
  className?: string;
}) => {
  return (
    <div className={`flex flex-col gap-[5px] ${className}`}>
      {hasTitle ? (
        <Skeleton
          width={"25%"}
          height={"20px"}
          baseColor={SKELETON_BASE_COLOR}
          highlightColor={SKELETON_HIGHLIGHT_COLOR}
        />
      ) : undefined}

      <Skeleton
        width={"100%"}
        height={"48px"}
        baseColor={SKELETON_BASE_COLOR}
        highlightColor={SKELETON_HIGHLIGHT_COLOR}
        className="rounded-lg"
      />
    </div>
  );
};
